/**
 * MKS-410 — "My requests": everything I reported, and what became of it.
 *
 * The list the reporter gets back for not having had to pick a project. It
 * answers the four questions the ticket names — what state is it in, who has
 * it, what did they say when they closed it, and when did any of that happen —
 * and nothing else: this is not a second triage surface.
 *
 * Scope is the SERVER's: `GET /api/helpdesk/my-requests` is strictly
 * `reporterUserId = me`, for an admin as much as for anybody else, so there is
 * no filtering to do here and no way for this component to widen it.
 */
import { Pill } from '../ui/Pill';
import { Spinner } from '../common/Spinner';
import { formatDisplayDateTime } from '../../utils/format';
import {
  reporterStatusHint,
  requestOutcomeText,
  requestStatusTone,
} from './requestFormat';
import type { HelpdeskRequestRecord } from '../../services/api/helpdeskRequests';

export interface MyRequestsListProps {
  requests: HelpdeskRequestRecord[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function MyRequestsList({ requests, isLoading, isError }: MyRequestsListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
        Failed to load your requests.
      </div>
    );
  }

  const rows = requests ?? [];

  if (rows.length === 0) {
    return (
      <div className="text-sm text-gray-500" data-testid="my-requests-empty">
        You haven&apos;t filed anything yet.
      </div>
    );
  }

  return (
    <ul
      className="bg-white border border-gray-200 rounded divide-y divide-gray-100"
      data-testid="my-requests"
    >
      {rows.map((request) => {
        const tone = requestStatusTone(request.status);
        const outcome = requestOutcomeText(request);
        return (
          <li
            key={request.id}
            className="px-3 py-3 space-y-1"
            data-testid={`my-request-${request.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <span className="text-sm text-ms-text-strong">
                <span className="text-gray-400 mr-2 text-xs">{request.typeName}</span>
                {request.title}
              </span>
              <Pill bg={tone.bg} fg={tone.fg}>
                {tone.label}
              </Pill>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <span>{reporterStatusHint(request)}</span>
              <span>·</span>
              {/* "Handled by" is the assignee under the name a reporter uses
                  for it. An unrouted request honestly has nobody. */}
              <span>
                {request.assignee ? `Handled by ${request.assignee.name}` : 'Unassigned'}
              </span>
              {request.projectName && (
                <>
                  <span>·</span>
                  <span>{request.projectName}</span>
                </>
              )}
              <span>·</span>
              <span>Filed {formatDisplayDateTime(request.createdAt)}</span>
              {request.resolvedAt && (
                <>
                  <span>·</span>
                  <span>Closed {formatDisplayDateTime(request.resolvedAt)}</span>
                </>
              )}
            </div>

            {outcome && (
              <p className="text-xs text-ms-text-secondary whitespace-pre-wrap">
                <span className="font-semibold">{outcome.label}:</span> {outcome.text}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
